/**
 * Pure display decisions for the temporary case list table, kept out of the component so
 * the node-environment Vitest suites can cover them without a DOM.
 */
import type { CaseListItem, CaseListPage } from '../api/contracts';

/**
 * Turns a camelCase/PascalCase enum wire value into spaced, Title-cased words for display —
 * `searchWarrant` → "Search Warrant", `inReview` → "In Review".
 *
 * A generic transform, not a closed label map, on purpose: the contract types these enums
 * as open strings so a CMS state the portal has not seen still renders rather than being
 * dropped (see contracts.ts). Acronym casing is not perfectly recovered, but a readable
 * approximation beats showing the raw wire token.
 */
export function humanizeEnum(value: string): string {
  const spaced = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Renders the status, adding the outcome to a completed case ("Complete — Preserved") so
 * outcomes without a disclosure summary can still be told apart.
 */
export function statusText(item: Pick<CaseListItem, 'status' | 'outcome'>): string {
  const status = humanizeEnum(item.status);
  return item.status === 'complete' && item.outcome != null && item.outcome !== ''
    ? `${status} — ${humanizeEnum(item.outcome)}`
    : status;
}

/** Which empty-state message an empty page shows. */
export type EmptyStateKind = 'moreResults' | 'firstPage' | 'pastEnd';

/**
 * Picks the message for a page with no rows, or `undefined` when the page has rows. A page
 * can be empty while more results remain (the CMS path drops non-E-Evidence rows after
 * paging), so that case asks the user to go on rather than saying they have no cases.
 */
export function emptyStateKind(
  page: Pick<CaseListPage, 'items' | 'hasMoreResults'>,
  historyLength: number,
): EmptyStateKind | undefined {
  if (page.items.length > 0) {
    return undefined;
  }

  if (page.hasMoreResults) {
    return 'moreResults';
  }

  return historyLength === 0 ? 'firstPage' : 'pastEnd';
}
